// ─── Fase 18 · pantalla de "simulando" (Monte Carlo) ────────────────────────
// Animación temática mientras se ejecuta una simulación: trayectorias de equity
// abriéndose hacia objetivo/límite y un histograma que se forma (ambos SON
// decorativos — animación abstracta, no datos afirmados). Se muestran solo cosas
// ciertas: % de progreso, tiempo transcurrido real y las etapas reales del
// motor. Sin cifras fabricadas. Textos vía i18n (useT). La duración la marca
// simTiming.simDuration() (5–10 s aleatorios por run). Se anima de forma
// imperativa sobre un ref para no re-renderizar en cada frame.
import { useEffect, useRef } from "react";
import { C } from "../lib/colors";
import { simDuration } from "../lib/simTiming";
import { useT } from "./LangContext";

const COL = { neutral: C.steel, pass: C.verdigris, fail: C.cinnabar };
const OP = { neutral: 0.16, pass: 0.55, fail: 0.5 };
const SVGNS = "http://www.w3.org/2000/svg";
const STAGE_COUNT = 8;

export default function SimulatingScreen({ compact = false }) {
  const rootRef = useRef(null);
  const { t } = useT();

  // Cadenas que usa el bucle imperativo, en un ref para que el efecto
  // (deps: [compact]) lea siempre el idioma actual sin reiniciar la animación.
  const strRef = useRef({});
  strRef.current = {
    stages: [
      t("sim_stage_1"), t("sim_stage_2"), t("sim_stage_3"), t("sim_stage_4"),
      t("sim_stage_5"), t("sim_stage_6"), t("sim_stage_7"), t("sim_stage_8"),
    ],
    complete: t("sim_complete"),
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cfg = compact
      ? { W: 656, H: 150, XL: 16, XR: 624, YT: 14, YB: 136, BASE: 84, TARGET: 34, FLOOR: 126, STEPS: 60, N: 30, hist: false }
      : { W: 656, H: 232, XL: 16, XR: 558, YT: 20, YB: 210, BASE: 132, TARGET: 48, FLOOR: 196, STEPS: 66, N: 44, hist: true, HX: 568, HMAX: 70, NB: 15 };

    const q = (r) => root.querySelector(`[data-r="${r}"]`);
    const paths = q("paths"), hist = q("hist"), dots = q("dots"), scan = q("scan");
    const fill = q("fill"), pct = q("pct"), stageEl = q("stage"), elapsed = q("elapsed"), pipsWrap = q("pips");

    const rnd = () => (Math.random() + Math.random() + Math.random() - 1.5) * 1.9;

    let pipEls = [];
    if (pipsWrap) {
      for (let i = 0; i < STAGE_COUNT; i++) {
        const p = document.createElement("div");
        p.style.cssText = `height:3px;flex:1;background:${C.whisper};transition:background .3s ease`;
        pipsWrap.appendChild(p);
        pipEls.push(p);
      }
    }

    const genPath = () => {
      const drift = Math.random() * 2.0 - 0.9, dx = (cfg.XR - cfg.XL) / cfg.STEPS;
      let y = cfg.BASE, cls = "neutral", cross = null; const pts = [];
      for (let s = 0; s <= cfg.STEPS; s++) {
        const x = cfg.XL + dx * s; pts.push([x, y]);
        if (!cross) {
          if (y <= cfg.TARGET) { cls = "pass"; cross = { x, y: cfg.TARGET, cls, frac: s / cfg.STEPS }; }
          else if (y >= cfg.FLOOR) { cls = "fail"; cross = { x, y: cfg.FLOOR, cls, frac: s / cfg.STEPS }; }
        }
        y = y - (drift + rnd());
        if (y < cfg.YT - 24) y = cfg.YT - 24;
        if (y > cfg.YB + 18) y = cfg.YB + 18;
      }
      return { pts, cls, cross, finalY: pts[pts.length - 1][1] };
    };

    const dur = simDuration();
    const data = []; for (let i = 0; i < cfg.N; i++) data.push(genPath());

    let raf = null; const timers = [];
    const els = [];
    data.forEach((d, i) => {
      const pl = document.createElementNS(SVGNS, "polyline");
      pl.setAttribute("points", d.pts.map((p) => p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" "));
      pl.setAttribute("fill", "none");
      pl.setAttribute("stroke", COL[d.cls]);
      pl.setAttribute("stroke-width", "1.1");
      pl.setAttribute("stroke-linecap", "round");
      pl.setAttribute("stroke-linejoin", "round");
      pl.setAttribute("opacity", OP[d.cls]);
      paths.appendChild(pl);
      const len = pl.getTotalLength();
      pl.style.strokeDasharray = len; pl.style.strokeDashoffset = len;
      const delay = (i / cfg.N) * dur * 0.55, draw = dur * 0.42;
      els.push({ pl, delay, draw });
      if (d.cross && dots) {
        timers.push(setTimeout(() => {
          const c = document.createElementNS(SVGNS, "circle");
          c.setAttribute("cx", d.cross.x.toFixed(1)); c.setAttribute("cy", d.cross.y);
          c.setAttribute("r", "2.6"); c.setAttribute("fill", COL[d.cross.cls]);
          c.style.opacity = "0"; c.style.transition = "opacity .35s ease";
          dots.appendChild(c);
          requestAnimationFrame(() => { c.style.opacity = d.cross.cls === "pass" ? "0.95" : "0.85"; });
        }, delay + d.cross.frac * draw));
      }
    });
    // eslint-disable-next-line no-unused-expressions
    paths.getBoundingClientRect();
    els.forEach((e) => {
      e.pl.style.transition = `stroke-dashoffset ${e.draw}ms linear ${e.delay}ms`;
      requestAnimationFrame(() => { e.pl.style.strokeDashoffset = "0"; });
    });

    let bars = [];
    if (cfg.hist && hist) {
      const binH = (cfg.YB - cfg.YT) / cfg.NB, counts = new Array(cfg.NB).fill(0);
      data.forEach((d) => { let b = Math.floor(((d.finalY - cfg.YT) / (cfg.YB - cfg.YT)) * cfg.NB); b = Math.max(0, Math.min(cfg.NB - 1, b)); counts[b]++; });
      const maxC = Math.max(1, ...counts);
      bars = counts.map((c, i) => {
        const cy = cfg.YT + (i + 0.5) * binH;
        const color = cy <= cfg.TARGET ? COL.pass : cy >= cfg.FLOOR ? COL.fail : C.brass;
        const r = document.createElementNS(SVGNS, "rect");
        r.setAttribute("x", cfg.HX); r.setAttribute("y", (cfg.YT + i * binH + 0.6).toFixed(1));
        r.setAttribute("height", (binH - 1.2).toFixed(1)); r.setAttribute("width", "0");
        r.setAttribute("fill", color); r.setAttribute("opacity", "0.72");
        hist.appendChild(r);
        return { r, full: (c / maxC) * cfg.HMAX };
      });
    }

    const t0 = performance.now();
    const loop = (now) => {
      const el = Math.max(0, now - t0); // clamp: en el 1er frame now puede ir por detrás de t0
      const p = Math.min(el / dur, 1);
      if (fill) fill.style.width = (p * 100).toFixed(1) + "%";
      if (pct) pct.textContent = Math.round(p * 100) + "%";
      if (elapsed) elapsed.textContent = "t+" + (el / 1000).toFixed(1) + "s";
      if (scan) { const sx = (cfg.XL + p * (cfg.XR - cfg.XL)).toFixed(1); scan.setAttribute("x1", sx); scan.setAttribute("x2", sx); }
      const si = Math.min(Math.floor(p * STAGE_COUNT), STAGE_COUNT - 1);
      if (stageEl) stageEl.textContent = strRef.current.stages[si];
      pipEls.forEach((pip, idx) => { pip.style.background = idx < si ? C.verdigris : idx === si ? C.brass : C.whisper; });
      bars.forEach((b) => b.r.setAttribute("width", (b.full * p).toFixed(1)));
      if (p < 1) { raf = requestAnimationFrame(loop); }
      else {
        pipEls.forEach((pip) => { pip.style.background = C.verdigris; });
        if (stageEl) stageEl.textContent = strRef.current.complete;
      }
    };
    raf = requestAnimationFrame(loop);

    return () => { if (raf) cancelAnimationFrame(raf); timers.forEach(clearTimeout); };
  }, [compact]);

  const line = (x1, y1, x2, y2, stroke, extra = {}) => (
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} {...extra} />
  );
  const chart = compact ? { W: 656, H: 150, XL: 16, XR: 624, T: 34, F: 126, B: 84 }
                        : { W: 656, H: 232, XL: 16, XR: 558, T: 48, F: 196, B: 132 };

  return (
    <div ref={rootRef} className="fg-panel" data-testid="simulating-screen"
         style={{ padding: compact ? "16px 18px" : "28px 30px 22px" }}
         role="img" aria-label={compact ? t("sim_kicker_compact") : t("sim_title")}>
      {!compact ? (
        <>
          <div style={{ fontFamily: "var(--mono)", fontSize: 12, letterSpacing: "0.24em", color: C.brass, textTransform: "uppercase", marginBottom: 10 }}>{t("sim_kicker")}</div>
          <div style={{ fontFamily: "var(--fraunces)", fontSize: 23, fontWeight: 500, color: C.bone, lineHeight: 1.2, marginBottom: 4 }}>{t("sim_title")}</div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: C.smoke, marginBottom: 14 }}>{t("sim_subtitle")}</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
            <span style={chip}>bootstrap</span>
            <span style={chip}>CRN</span>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.2em", color: C.brass, textTransform: "uppercase" }}>{t("sim_kicker_compact")}</span>
          <span data-r="elapsed" style={{ fontFamily: "var(--mono)", fontSize: 11, color: C.smoke }}>t+0.0s</span>
        </div>
      )}

      <svg viewBox={`0 0 ${chart.W} ${chart.H}`} preserveAspectRatio="xMidYMid meet"
           style={{ width: "100%", height: "auto", display: "block", background: C.ink, border: `1px solid ${C.dust}` }}>
        {line(chart.XL, chart.T, chart.XR, chart.T, C.brass, { strokeWidth: 0.8, strokeDasharray: "4 5", opacity: 0.7 })}
        <text x={chart.XR - 4} y={chart.T - 4} textAnchor="end" fontFamily="var(--mono)" fontSize="11" fill={C.brass}>{t("sim_target")}</text>
        {line(chart.XL, chart.F, chart.XR, chart.F, C.cinnabar, { strokeWidth: 0.8, strokeDasharray: "4 5", opacity: 0.7 })}
        <text x={chart.XR - 4} y={chart.F - 4} textAnchor="end" fontFamily="var(--mono)" fontSize="11" fill={C.cinnabar}>{t("sim_limit")}</text>
        {line(chart.XL, chart.B, chart.XR, chart.B, C.haze, { strokeWidth: 0.7, opacity: 0.5 })}
        <text x={chart.XL} y={chart.B - 6} fontFamily="var(--mono)" fontSize="11" fill={C.smoke}>{t("sim_start_capital")}</text>
        {!compact && <text x="604" y="14" textAnchor="middle" fontFamily="var(--mono)" fontSize="11" fill={C.smoke}>{t("sim_distribution")}</text>}
        <line data-r="scan" x1={chart.XL} y1={compact ? 14 : 20} x2={chart.XL} y2={compact ? 136 : 210} stroke={C.brass} strokeWidth="1" opacity="0.22" />
        <g data-r="paths" />
        {!compact && <g data-r="hist" />}
        <g data-r="dots" />
      </svg>

      {!compact && (
        <div style={{ display: "flex", justifyContent: "flex-start", fontFamily: "var(--mono)", border: `1px solid ${C.dust}`, borderBottom: "none", padding: "9px 14px", marginTop: 14 }}>
          <span data-r="pct" style={{ fontSize: 15, color: C.bone, fontWeight: 500 }}>0%</span>
        </div>
      )}

      <div style={{ height: compact ? 4 : 5, background: C.leather, border: `1px solid ${C.dust}`, position: "relative", overflow: "hidden", marginTop: compact ? 10 : 0, marginBottom: compact ? 10 : 16 }}>
        <div data-r="fill" style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 0, background: C.brass, transition: "width .12s linear" }} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 18, marginBottom: compact ? 0 : 12 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.brass, flex: "none", animation: "fg-blink 1.1s ease-in-out infinite" }} />
        <span data-r="stage" style={{ fontFamily: "var(--mono)", fontSize: 13, color: C.bone }}>{t("sim_starting")}</span>
      </div>

      {!compact && <div data-r="pips" style={{ display: "flex", gap: 6, marginBottom: 18 }} />}

      {!compact && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: `1px solid ${C.dust}`, paddingTop: 13 }}>
          <span data-r="elapsed" style={{ fontFamily: "var(--mono)", fontSize: 11, color: C.smoke, letterSpacing: "0.05em" }}>t+0.0s</span>
        </div>
      )}
    </div>
  );
}

const chip = { fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.04em", color: C.steel, border: `1px solid ${C.dust}`, padding: "3px 9px", background: C.ink };
